// src/components/Auth/Login.js
// Updated:
// - Light theme + brand gradient to match LandingPage + Signup
// - Focused login experience (Signup handled at /signup)
// - Keeps password reset
// - Keeps safe redirect (location.state.from)

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { executeRecaptcha } from '../../services/recaptcha';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Password reset
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { login, resetPassword, currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) navigate('/dashboard', { replace: true });
  }, [currentUser, navigate]);

  const validate = () => {
    setError('');
    setSuccess('');

    if (showReset) {
      if (!resetEmail.trim()) {
        setError('Please enter the email you used to sign up.');
        return false;
      }
      return true;
    }

    if (!email.trim()) {
      setError('Email is required.');
      return false;
    }

    if (!password.trim()) {
      setError('Password is required.');
      return false;
    }

    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Clear onboarding check so users can re-trigger onboarding if needed
      try {
        sessionStorage.removeItem('onboarding_checked');
      } catch {}

      // Optional recaptcha (won't block login if not configured)
      try {
        await executeRecaptcha('login');
      } catch (err) {
        console.warn('Recaptcha failed (continuing):', err?.message);
      }

      await login(email.trim().toLowerCase(), password.trim());
      setSuccess('Welcome back! Redirecting…');

      const redirectTo = location?.state?.from?.pathname || '/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      console.error('Login error:', err);

      let msg = 'Failed to log in. Please try again.';
      const code = err?.code || '';

      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
        msg = 'Incorrect email or password.';
      } else if (code === 'auth/user-not-found') {
        msg = 'No account found for that email. Create an account to get started.';
      } else if (typeof err?.message === 'string' && err.message.length) {
        msg = err.message;
      }

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await resetPassword(resetEmail.trim().toLowerCase());
      setResetSent(true);
      setSuccess('Password reset email sent. Check your inbox.');
    } catch (err) {
      console.error('Reset error:', err);
      setError('Could not send reset email. Please verify the address and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      {/* soft gradient accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-pink-200/40 blur-3xl" />
        <div className="absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-indigo-200/40 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-2xl border border-gray-200 p-8">
          <div className="text-center mb-8">
            <img
              src="/Infleuince_Landing.png"
              alt="Infleuince"
              className="h-10 w-auto mx-auto mb-5"
              loading="eager"
            />

            <h1 className="text-3xl font-black text-gray-900">
              {showReset ? 'Reset your password' : 'Log in'}
            </h1>
            <p className="text-gray-600 text-sm mt-2">
              {showReset
                ? 'We’ll send you a link to reset your password.'
                : 'Pick up where you left off.'}
            </p>
          </div>

          {(error || success) && (
            <div
              className={`mb-6 p-3 rounded-lg text-sm border ${
                error
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-green-50 border-green-200 text-green-700'
              }`}
            >
              {error || success}
            </div>
          )}

          {!showReset ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-gray-500" /> Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400/50 focus:border-pink-500 text-gray-900 placeholder-gray-500 shadow-sm"
                  placeholder="you@creator.com"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <Lock className="w-4 h-4 mr-2 text-gray-500" /> Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full p-3 pr-11 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400/50 focus:border-pink-500 text-gray-900 placeholder-gray-500 shadow-sm"
                    placeholder="Your password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setShowReset(true);
                    setResetEmail(email || '');
                    setResetSent(false);
                    setError('');
                    setSuccess('');
                  }}
                  className="text-sm font-bold text-pink-700 hover:text-pink-800 hover:underline"
                >
                  Forgot password?
                </button>

                <Link
                  to="/help-center"
                  className="text-sm font-bold text-gray-600 hover:text-pink-700"
                >
                  Need help?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white py-3.5 font-extrabold shadow-lg hover:brightness-105 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Logging in…
                  </>
                ) : (
                  <>
                    Log in
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="text-center text-sm text-gray-600">
                Not invited yet?{' '}
                <Link to="/waitlist" className="text-pink-700 font-extrabold hover:underline">
                  Join the waitlist
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleReset} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-gray-500" /> Email
                </label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400/50 focus:border-pink-500 text-gray-900 placeholder-gray-500 shadow-sm"
                  placeholder="you@creator.com"
                  disabled={loading || resetSent}
                />
              </div>

              <button
                type="submit"
                disabled={loading || resetSent}
                className="w-full rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white py-3.5 font-extrabold shadow-lg hover:brightness-105 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending…
                  </>
                ) : resetSent ? (
                  'Reset link sent'
                ) : (
                  'Send reset link'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowReset(false);
                  setResetSent(false);
                  setError('');
                  setSuccess('');
                }}
                className="w-full py-3 rounded-xl font-extrabold border-2 border-gray-200 text-gray-800 bg-white hover:border-pink-400"
              >
                Back to login
              </button>

              <p className="text-xs text-gray-500 leading-relaxed">
                Need more help? Visit the{' '}
                <Link to="/help-center" className="text-pink-700 font-semibold hover:underline">Help Center</Link>.
              </p>
            </form>
          )}
        </div>

        <div className="mt-5 text-center text-xs text-gray-500">
          <Link to="/privacy-policy" className="hover:underline">Privacy</Link>
          {' '}·{' '}
          <Link to="/terms-of-service" className="hover:underline">Terms</Link>
        </div>
      </div>
    </div>
  );
}